import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import {
    AccountInventorySettingState,
    NewNodeAccountInventorySetting,
    EditNodeAccountInventorySetting,
    SelectedNodeGroups,
    SelectedNodeDomains,
} from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import ScreenUrl from '../../../router/url';
import router from '../../../router';

export const actions: ActionTree<AccountInventorySettingState, RootState> = {
    /**
     * アカウント棚卸設定一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
            word: searchItems.word,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.AccountInventorySetting.SEARCH, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.SEARCH_FAILURE, params);
        }
    },

    /**
     * アカウント棚卸設定新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewBusinessSystem} newNodeAccountInventorySetting 新規アカウント棚卸設定情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newNodeAccountInventorySetting: NewNodeAccountInventorySetting) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.AccountInventorySetting.CREATE,
                    params: newNodeAccountInventorySetting,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AccountInventorySetting.CREATED);
                dispatch('toastMessage', `${newNodeAccountInventorySetting.name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * アカウント棚卸設定の詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id アカウント棚卸設定ID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.AccountInventorySetting.SHOW.replace(':id', id.toString()) },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.SHOW_FAILURE, params);
        }
    },

    /**
     * アカウント棚卸設定更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditNodeAccountInventorySetting} editNodeAccountInventorySetting 更新するノード情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, editNodeAccountInventorySetting: EditNodeAccountInventorySetting) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.AccountInventorySetting.UPDATE.replace(
                        ':id',
                        editNodeAccountInventorySetting.id.toString()
                    ),
                    params: editNodeAccountInventorySetting,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AccountInventorySetting.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * アカウント棚卸即時実行
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditNodeAccountInventorySetting} editNodeAccountInventorySetting 更新するノード情報
     * @returns {Promise<void>}
     */
    async executeInventory({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.EXECUTE_INVENTORY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.AccountInventorySetting.EXECUTE_INVENTORY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.EXECUTE_INVENTORY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AccountInventorySetting.EXECUTED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.EXECUTE_INVENTORY_FAILURE, params);
        }
    },

    /**
     * 新規作成・更新前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままのエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit
     * @returns {Promise<void>}
     */
    async clearError({ commit }) {
        commit(types.CLEAR_ERROR_ACCOUNT_INVENTORY_SETTING);
    },

    /**
     * アカウント棚卸設定削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id アカウント棚卸設定ID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.AccountInventorySetting.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AccountInventorySetting.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * アカウント棚卸設定の未追加ノードグループ一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async searchUnassignedNodeGroups({ dispatch, commit }, { id, pageNumber, perPage, sort }) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_UNASSIGNED_NODE_GROUPS_START);
            const resNodeGroupsData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.AccountInventorySetting.SEARCH_UNASSIGNED_NODE_GROUPS.replace(':id', id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resNodeGroupsData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_UNASSIGNED_NODE_GROUPS_SUCCESS, resNodeGroupsData);
            }
        } catch (resNodeGroupsData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resNodeGroupsData.title,
                errorDetails: resNodeGroupsData.errors,
            };
            commit(types.SEARCH_UNASSIGNED_NODE_GROUPS_FAILURE, params);
        }
    },

    /**
     * 棚卸対象ノードグループ追加
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {SelectedNodeGroups} newNodeGroups 追加する棚卸対象ノードグループ情報
     * @returns {Promise<void>}
     */
    async addTargetNodeGroups({ dispatch, commit, rootState }, newNodeGroups: SelectedNodeGroups) {
        const payload = {
            id: newNodeGroups.id,
            node_group_ids: newNodeGroups.nodeGroupIds,
        };
        try {
            commit(types.ADD_NODE_GROUPS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.AccountInventorySetting.ADD_NODE_GROUPS.replace(':id', newNodeGroups.id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ADD_NODE_GROUPS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AccountInventorySetting.ADD_NODE_GROUPS);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.ADD_NODE_GROUPS_FAILURE, params);
        }
    },

    /**
     * 棚卸対象ノードグループ除外
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {SelectedNodeGroups} removeNodeGroups 除外するノード情報
     * @returns {Promise<void>}
     */
    async removeTargetNodeGroups({ dispatch, commit, rootState }, removeNodeGroups: SelectedNodeGroups) {
        const payload = {
            id: removeNodeGroups.id,
            node_group_ids: removeNodeGroups.nodeGroupIds,
        };
        try {
            commit(types.REMOVE_NODE_GROUPS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.AccountInventorySetting.REMOVE_NODE_GROUPS.replace(
                        ':id',
                        removeNodeGroups.id.toString()
                    ),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.REMOVE_NODE_GROUPS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AccountInventorySetting.REMOVE_NODE_GROUPS);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.REMOVE_NODE_GROUPS_FAILURE, params);
        }
    },

    /**
     * アカウント棚卸設定の未追加ノードドメイン一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async searchUnassignedNodeDomains({ dispatch, commit }, { id, pageNumber, perPage, sort }) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_UNASSIGNED_NODE_DOMAINS_START);
            const resNodeDomainsData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.AccountInventorySetting.SEARCH_UNASSIGNED_NODE_DOMAINS.replace(':id', id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resNodeDomainsData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_UNASSIGNED_NODE_DOMAINS_SUCCESS, resNodeDomainsData);
            }
        } catch (resNodeDomainsData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resNodeDomainsData.title,
                errorDetails: resNodeDomainsData.errors,
            };
            commit(types.SEARCH_UNASSIGNED_NODE_DOMAINS_FAILURE, params);
        }
    },

    /**
     * 棚卸対象ノードドメイン追加
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {SelectedNodeDomains} newNodeDomains 追加する棚卸対象ノードドメイン情報
     * @returns {Promise<void>}
     */
    async addTargetNodeDomains({ dispatch, commit, rootState }, newNodeDomains: SelectedNodeDomains) {
        const payload = {
            id: newNodeDomains.id,
            node_domain_ids: newNodeDomains.nodeDomainIds,
        };
        try {
            commit(types.ADD_NODE_DOMAINS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.AccountInventorySetting.ADD_NODE_DOMAINS.replace(':id', newNodeDomains.id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ADD_NODE_DOMAINS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AccountInventorySetting.ADD_NODE_DOMAINS);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.ADD_NODE_DOMAINS_FAILURE, params);
        }
    },

    /**
     * 棚卸対象ノードドメイン除外
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {SelectedNodeDomains} removeNodeDomains 除外するノード情報
     * @returns {Promise<void>}
     */
    async removeTargetNodeDomains({ dispatch, commit, rootState }, removeNodeDomains: SelectedNodeDomains) {
        const payload = {
            id: removeNodeDomains.id,
            node_domain_ids: removeNodeDomains.nodeDomainIds,
        };
        try {
            commit(types.REMOVE_NODE_DOMAINS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.AccountInventorySetting.REMOVE_NODE_DOMAINS.replace(
                        ':id',
                        removeNodeDomains.id.toString()
                    ),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.REMOVE_NODE_DOMAINS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AccountInventorySetting.REMOVE_NODE_DOMAINS);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.REMOVE_NODE_DOMAINS_FAILURE, params);
        }
    },

    /**
     * 棚卸対象ノードドメインのセキュリティグループ更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {SecurityGroup} securityGroups 更新するセキュリティグループ情報
     * @returns {Promise<void>}
     */
    async updateSecurityGroups({ dispatch, commit, rootState }, { id, domain_id, securityGroups }) {
        const payload = {
            id: id,
            domain_id: domain_id,
            security_groups: securityGroups,
        };
        try {
            commit(types.UPDATE_SECURITY_GROUPS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.AccountInventorySetting.UPDATE_SECURITY_GROUPS.replace(':id', id.toString()).replace(
                        ':domain_id',
                        domain_id.toString()
                    ),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SECURITY_GROUPS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AccountInventorySetting.UPDATED_SECURITY_GROUPS);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_SECURITY_GROUPS_FAILURE, params);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
